import * as THREE from "three";
import { NeonTube } from "../neon/NeonTube";
import type { Tool, ToolContext, PointerInfo } from "./Tool";

const MIN_SAMPLE_DIST = 0.22;
const MIN_POINTS_TO_KEEP = 2;

/**
 * Freehand draw. Click-drag on the canvas to extrude a neon tube along the
 * drawing plane. Lift the pointer to finish. Samples are decimated so the
 * resulting curve stays smooth and cheap to tessellate.
 */
export class DrawTool implements Tool {
  readonly id = "draw";
  readonly name = "Draw";
  readonly grabsPointer = true;
  readonly hint = "Click and drag to draw a neon tube. Hold shift to draw in straight segments.";
  readonly icon = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M4 20c6-1 8-8 16-14" />
      <circle cx="4" cy="20" r="1.2" fill="currentColor" />
    </svg>`;

  private tube: NeonTube | null = null;
  private lastPoint: THREE.Vector3 | null = null;
  private shiftMode = false;

  onPointerDown(info: PointerInfo, ctx: ToolContext): void {
    if (!info.planePoint) return;
    this.shiftMode = info.event.shiftKey;
    this.tube = new NeonTube({
      color: ctx.getBrushColor(),
      thickness: ctx.getBrushThickness(),
      intensity: ctx.getBrushIntensity(),
    });
    this.tube.addPoint(info.planePoint);
    // Add a second point so the tube is renderable on first render.
    this.tube.addPoint(info.planePoint.clone().add(new THREE.Vector3(0.001, 0, 0)));
    ctx.design.add(this.tube);
    this.lastPoint = info.planePoint.clone();
  }

  onPointerMove(info: PointerInfo, ctx: ToolContext): void {
    if (!this.tube || !info.planePoint) return;
    const p = info.planePoint;

    if (this.shiftMode) {
      // Straight-segment preview: keep only first point + current.
      if (this.tube.points.length >= 2) {
        this.tube.setPoint(this.tube.points.length - 1, p);
      }
      return;
    }

    if (!this.lastPoint || p.distanceTo(this.lastPoint) >= MIN_SAMPLE_DIST) {
      this.tube.addPoint(p);
      this.lastPoint = p.clone();
      ctx.design.touch();
    } else {
      // Smoothly update the trailing point for fluid feedback.
      const lastIdx = this.tube.points.length - 1;
      this.tube.setPoint(lastIdx, p);
    }
  }

  onPointerUp(_info: PointerInfo, ctx: ToolContext): void {
    if (!this.tube) return;
    if (this.tube.points.length < MIN_POINTS_TO_KEEP) {
      ctx.design.remove(this.tube);
    } else {
      ctx.toast("Neon tube created");
    }
    this.tube = null;
    this.lastPoint = null;
    this.shiftMode = false;
    ctx.design.touch();
  }
}
